import { Fragment } from 'react'
import { Link } from '@inertiajs/react'
import { Avatar } from './Avatar'
import { Dropdown } from './Dropdown'
import { DropdownAction } from './DropdownAction'
import { Icon, type IconName } from './Icon'

// ---------------------------------------------------------------------------
// UserMenu — the top-bar identity chip + avatar dropdown from the prototype.
//
// Surface-AGNOSTIC and fully props-driven: every string (display name, meta
// line, item labels, hints, the trigger's aria-label) is supplied by the
// caller, so this component carries NO Norwegian copy and is safe to ship in
// any of the three bundles (including the public, sessionless register).
//
// It is a thin composition over the shared <Dropdown> primitive — all the
// hard parts (portal positioning, outside-click / Escape close, roving focus,
// focus return to the trigger, aria-haspopup / aria-expanded wiring) are
// inherited, never re-implemented here. The trigger is the identity chip
// (`.topbar__user`); the panel is the `.usermenu` card with only the
// caller-supplied items rendered as `.dd__item` rows.
//
// The admin / portal shells wire this with their own i18n'd props; this file
// stays dumb.
// ---------------------------------------------------------------------------

export interface UserMenuItem {
  /** Stable React key + identity for the item. */
  key: string
  /** Visible label (caller-supplied, already localized). */
  label: string
  /** Optional leading glyph. */
  icon?: IconName
  /** Invoked when the item is activated (ignored when `href` is set). */
  onSelect?: () => void
  /**
   * When set, the item renders as an Inertia <Link> to this path (styled like
   * a `.dd__item`) instead of a button. Navigation replaces `onSelect`.
   */
  href?: string
  /** Render with the destructive `.dd__item--danger` treatment (e.g. log out). */
  danger?: boolean
  /** Natively disable the item (not focusable, not clickable). */
  disabled?: boolean
  /** Small muted trailing text, e.g. a "coming later" note for a deferred action. */
  hint?: string
  /**
   * Draw a `.dd__sep` divider immediately BEFORE this item. This is how a
   * caller groups items into sections — set it on the first item of each new
   * group. (No leading separator is drawn on the very first item.)
   */
  separatorBefore?: boolean
}

export interface UserMenuProps {
  /** Display name — shown in the chip and its avatar. */
  name: string
  /** Muted line under the chip's name (e.g. role + organisation, or an email). */
  meta?: string
  /** Items in render order. Group them with `separatorBefore` on an item. */
  items: UserMenuItem[]
  /** aria-label for the trigger chip (caller passes the localized "user menu"). */
  triggerLabel?: string
  /** Which corner the panel anchors to. Defaults to 'bottom-end'. */
  placement?: 'bottom-end' | 'bottom-start'
}

// Shared inner content for both the <button> and <Link> item variants: an
// optional leading icon, the label, and an optional hint. A hinted item stacks
// the hint as a muted sublabel UNDER the label (not trailing) so a "coming
// later" note never collides with / clips a long label on the narrow panel,
// in any locale (nb/nn/en).
function ItemBody({ item }: { item: UserMenuItem }) {
  return (
    <>
      {item.icon && <Icon name={item.icon} size={15} aria-hidden="true" />}
      {item.hint ? (
        <span className="dd__item-text">
          <span>{item.label}</span>
          <span className="usermenu__hint">{item.hint}</span>
        </span>
      ) : (
        item.label
      )}
    </>
  )
}

/**
 * Top-bar user menu: an identity chip that opens an avatar dropdown of
 * caller-supplied actions. Generic and surface-agnostic — all copy arrives via
 * props.
 *
 * @example
 * <UserMenu
 *   name={identity.display_name}
 *   meta={`${identity.role_label} · ${identity.department_name}`}
 *   triggerLabel={t('shell.user_menu')}
 *   items={[
 *     { key: 'profile', label: t('shell.user.profile'), icon: 'user', onSelect: openProfile },
 *     { key: 'logout', label: t('shell.user.logout'), icon: 'x', danger: true,
 *       separatorBefore: true, onSelect: logout },
 *   ]}
 * />
 */
export function UserMenu({
  name,
  meta,
  items,
  triggerLabel,
  placement = 'bottom-end',
}: UserMenuProps) {
  const chip = (
    <button type="button" className="topbar__user" aria-label={triggerLabel}>
      <Avatar name={name} size={28} />
      <div>
        <div className="topbar__user-name">{name}</div>
        {meta && <div className="topbar__user-role">{meta}</div>}
      </div>
      <Icon name="chevronD" size={14} aria-hidden="true" />
    </button>
  )

  return (
    <Dropdown trigger={chip} placement={placement}>
      <div className="usermenu">
        {items.map((item) => (
          <Fragment key={item.key}>
            {item.separatorBefore && <div className="dd__sep" />}
            {item.href !== undefined ? (
              <DropdownAction
                asChild
                role="menuitem"
                data-color={item.danger ? 'danger' : undefined}
                className={
                  item.danger ? 'dd__item dd__item--danger' : 'dd__item'
                }
              >
                <Link href={item.href}>
                  <ItemBody item={item} />
                </Link>
              </DropdownAction>
            ) : (
              <DropdownAction
                type="button"
                role="menuitem"
                data-color={item.danger ? 'danger' : undefined}
                className={
                  item.danger ? 'dd__item dd__item--danger' : 'dd__item'
                }
                disabled={item.disabled}
                onClick={item.onSelect}
              >
                <ItemBody item={item} />
              </DropdownAction>
            )}
          </Fragment>
        ))}
      </div>
    </Dropdown>
  )
}
